import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "@/context/AuthContext";
import { Trash2, MessageSquare, Target, Sparkles, ArrowRight, Search } from "lucide-react";
import { toast } from "sonner";

const KIND_META = {
    reply: { label: "Reply", icon: MessageSquare, cls: "bg-signal/10 text-signal border-signal/30" },
    outreach: { label: "Outreach", icon: Target, cls: "bg-purple-500/10 text-purple-300 border-purple-500/30" },
    coach: { label: "Coach", icon: Sparkles, cls: "bg-amber-500/10 text-amber-300 border-amber-500/30" },
};
const METHOD_LABELS = { nepq: "NEPQ", spin: "SPIN", challenger: "Challenger", sandler: "Sandler", meddic: "MEDDIC", gap: "Gap Selling", consultative: "Consultative", straight_line: "Straight Line" };

export default function History() {
    const { authHeaders } = useAuth();
    const [convs, setConvs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");
    const [q, setQ] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/conversations`, { headers: authHeaders() });
            setConvs(res.data || []);
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []); // eslint-disable-line

    const del = async (e, id) => {
        e.preventDefault(); e.stopPropagation();
        if (!window.confirm("Delete this conversation? This cannot be undone.")) return;
        try {
            await axios.delete(`${API}/conversations/${id}`, { headers: authHeaders() });
            setConvs((prev) => prev.filter((c) => c.id !== id));
            toast.success("Deleted");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Delete failed");
        }
    };

    const filtered = convs.filter((c) => {
        if (filter !== "all" && (c.kind || "reply") !== filter) return false;
        if (q) {
            const needle = q.toLowerCase();
            const hay = `${c.title || ""} ${c.conversation || ""} ${c.industry || ""} ${c.tone || ""}`.toLowerCase();
            if (!hay.includes(needle)) return false;
        }
        return true;
    });

    return (
        <div data-testid="history-page">
            <div className="mb-8">
                <div className="text-xs font-mono uppercase tracking-[0.2em] text-signal mb-2">/ HISTORY</div>
                <h1 className="font-heading font-black text-4xl md:text-5xl tracking-tight">Your conversations.</h1>
                <p className="text-zinc-400 mt-2">Every reply and outreach you've drafted — pick up where you left off and keep refining with the AI.</p>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap items-center gap-3 mb-6">
                <div className="flex items-center gap-1.5 border border-zinc-900 rounded-md bg-[#0c0c0c] p-1">
                    {["all", "reply", "outreach"].map((k) => (
                        <button
                            key={k}
                            onClick={() => setFilter(k)}
                            data-testid={`history-filter-${k}`}
                            className={`px-3 py-1.5 rounded text-xs font-mono uppercase tracking-widest transition-colors ${filter === k ? "bg-signal text-white" : "text-zinc-400 hover:text-white"}`}
                        >
                            {k}
                        </button>
                    ))}
                </div>
                <div className="flex-1 min-w-[200px] relative">
                    <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search title, content, tone, industry…"
                        data-testid="history-search"
                        className="w-full bg-[#0c0c0c] border border-zinc-900 rounded-md text-white pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-signal focus:border-signal"
                    />
                </div>
            </div>

            {loading ? (
                <div className="text-zinc-500" data-testid="history-loading">Loading…</div>
            ) : filtered.length === 0 ? (
                <div className="border border-zinc-900 border-dashed p-16 text-center text-zinc-500" data-testid="history-empty">
                    {convs.length === 0 ? "No conversations yet. Start one from New Reply or Cold Outreach." : "No matches for your filter."}
                </div>
            ) : (
                <div className="space-y-3" data-testid="history-list">
                    {filtered.map((c) => {
                        const meta = KIND_META[c.kind || "reply"] || KIND_META.reply;
                        const KindIcon = meta.icon;
                        const msgCount = (c.messages || []).length;
                        return (
                            <Link
                                to={`/app/chat/${c.id}`}
                                key={c.id}
                                data-testid={`history-item-${c.id}`}
                                className="block border border-zinc-900 bg-[#0c0c0c] rounded-md p-5 hover:border-signal/50 transition-colors group"
                            >
                                <div className="flex items-start gap-4">
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2 flex-wrap mb-1.5">
                                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-mono uppercase tracking-widest border ${meta.cls}`}>
                                                <KindIcon className="w-3 h-3" /> {meta.label}
                                            </span>
                                            {c.methodology && (
                                                <span className="text-[10px] font-mono uppercase tracking-widest text-signal">{METHOD_LABELS[c.methodology] || c.methodology}</span>
                                            )}
                                            <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                                                {c.tone} · {c.industry} · {c.platform}
                                            </span>
                                            <span className="text-[10px] font-mono text-zinc-600">· {msgCount} {msgCount === 1 ? "msg" : "msgs"}</span>
                                        </div>
                                        <h2 className="font-heading font-bold text-base text-white truncate mb-1">{c.title || "(untitled)"}</h2>
                                        <div className="text-xs text-zinc-500 truncate">{c.conversation?.slice(0, 200)}</div>
                                    </div>
                                    <div className="flex flex-col items-end gap-2 shrink-0">
                                        <span className="text-[10px] font-mono text-zinc-600">{new Date(c.created_at).toLocaleDateString()}</span>
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={(e) => del(e, c.id)}
                                                data-testid={`history-delete-${c.id}`}
                                                className="text-zinc-500 hover:text-red-400 p-1.5 border border-transparent hover:border-red-400/30 rounded-md transition-colors"
                                                title="Delete"
                                            >
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                            <span className="text-xs text-zinc-500 group-hover:text-signal inline-flex items-center gap-1 transition-colors">
                                                Open <ArrowRight className="w-3 h-3" />
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </Link>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
